import React, { useState, useEffect, useRef, Children } from 'react';

/**
 * Carousel — a calm, auto-advancing slideshow used across Event Readiness:
 * the "things that need your attention" card and Milo's recommendations both
 * show ONE item at a time. ‹ › arrows + dots navigate; a manual move restarts
 * the auto-advance timer. Honors prefers-reduced-motion (no auto-advance).
 * A single child renders bare (no controls); zero children render nothing.
 * Slides keep their own buttons/handlers — this only changes the presentation.
 */
export default function Carousel({ children, interval = 5200, className = '', ariaLabel = 'Slideshow' }) {
  const slides = Children.toArray(children).filter(Boolean);
  const n = slides.length;
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const reduce = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const start = () => {
    clearInterval(timer.current);
    if (n > 1 && !reduce) timer.current = setInterval(() => setIdx((p) => (p + 1) % n), interval);
  };
  // Keep idx in range when slides are added/removed (e.g. a nudge is dismissed).
  useEffect(() => { setIdx((p) => (p >= n ? Math.max(0, n - 1) : p)); start(); return () => clearInterval(timer.current); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [n]);

  if (n === 0) return null;
  const cur = Math.min(idx, n - 1);
  const go = (i) => { setIdx(((i % n) + n) % n); start(); };

  return (
    <div className={`ero-car ${className}`} role="group" aria-roledescription="carousel" aria-label={ariaLabel}>
      <div className="ero-car-stage" key={cur}>{slides[cur]}</div>
      {n > 1 && (
        <div className="ero-car-nav">
          <button type="button" className="ero-car-arrow" aria-label="Previous" onClick={() => go(cur - 1)}>‹</button>
          <div className="ero-car-dots">
            {slides.map((_, j) => (
              <button key={j} type="button" className={`ero-car-dot${j === cur ? ' on' : ''}`}
                      aria-label={`Go to item ${j + 1}`} aria-current={j === cur ? 'true' : undefined}
                      onClick={() => go(j)} />
            ))}
          </div>
          <button type="button" className="ero-car-arrow" aria-label="Next" onClick={() => go(cur + 1)}>›</button>
        </div>
      )}
    </div>
  );
}
